/**
 * CaptionComposer — the "Insert a video caption" window on the finished-video
 * screen. Two boxes:
 *   1) an always-editable caption field + an Autogenerate button (Claude Haiku,
 *      fed the video's plan card → a viral caption in the plan's language).
 *   2) a refine-with-a-prompt input that only APPEARS after the first autogenerate
 *      (e.g. "make it funnier", "add more hashtags") — chat-style editing.
 * The caption is edited live via `onChange` so SocialPublish posts whatever's here.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, X, ArrowUp } from 'lucide-react';
import { generateCaption } from '@/v2/caption';
import type { V2VideoPlan } from '@/api/v2Service';

export function CaptionComposer({
  value,
  onChange,
  plan,
  videoTitle,
  title,
  onTitleChange,
  showTitle,
  onClose,
}: {
  value: string;
  onChange: (v: string) => void;
  plan?: V2VideoPlan | null;
  /** The video's title/prompt — context for manual videos that have no plan card. */
  videoTitle?: string;
  /** The editable "Video title (for YouTube)" value — shown + AI-generated when a
   *  title platform (YouTube) is connected. */
  title?: string;
  onTitleChange?: (v: string) => void;
  showTitle?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('v2');
  const [busy, setBusy] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false); // box 2 hidden until first autogenerate
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState('');

  const run = async (instruction?: string) => {
    setBusy(true);
    setError('');
    try {
      const res = await generateCaption({
        plan,
        videoTitle,
        instruction,
        currentCaption: value || undefined,
        wantTitle: !!showTitle,
        currentTitle: title || undefined,
      });
      if (res.caption) onChange(res.caption);
      if (showTitle && res.title && onTitleChange) onTitleChange(res.title);
      setShowPrompt(true);
      setPrompt('');
    } catch {
      setError(t('captionComposer.error'));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-surface border border-glass-border shadow-2xl p-5 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-surface-light border border-glass-border flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
          aria-label={t('captionComposer.done')}
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-base font-bold text-text-primary flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-primary" /> {t('captionComposer.title')}
        </h3>
        <p className="text-xs text-text-tertiary mb-3">{t('captionComposer.hint')}</p>

        {/* Video title — shown when a title platform (YouTube) is connected; generated
            together with the caption by the same Autogenerate call. */}
        {showTitle && (
          <input
            value={title || ''}
            onChange={(e) => onTitleChange?.(e.target.value)}
            placeholder={t('socialPublish.titlePlaceholder')}
            maxLength={100}
            className="mb-2 w-full bg-surface-light border border-glass-border rounded-xl px-3 py-2.5 text-sm font-semibold text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50"
          />
        )}

        {/* Box 1 — editable caption + autogenerate */}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('captionComposer.placeholder')}
          rows={4}
          className="w-full bg-surface-light border border-glass-border rounded-xl px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50 resize-none"
        />
        <button
          onClick={() => void run()}
          disabled={busy}
          className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-bold hover:opacity-95 disabled:opacity-60 transition-opacity"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? t('captionComposer.generating') : value ? t('captionComposer.regenerate') : t('captionComposer.generate')}
        </button>
        {error && <p className="mt-2 text-xs text-error text-center">{error}</p>}

        {/* Box 2 — refine via prompt (appears only after the first autogenerate) */}
        {showPrompt && (
          <div className="mt-3 animate-fade-in">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-text-tertiary mb-1">
              {t('captionComposer.refineLabel')}
            </label>
            <div className="flex items-center gap-2">
              <input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && prompt.trim() && !busy) void run(prompt.trim()); }}
                placeholder={t('captionComposer.refinePlaceholder')}
                className="flex-1 bg-surface-light border border-glass-border rounded-xl px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50"
              />
              <button
                onClick={() => { if (prompt.trim() && !busy) void run(prompt.trim()); }}
                disabled={busy || !prompt.trim()}
                className="w-9 h-9 shrink-0 rounded-xl bg-primary text-white flex items-center justify-center hover:opacity-90 disabled:opacity-50 transition-opacity"
                aria-label={t('captionComposer.refineLabel')}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-4 w-full px-4 py-2.5 rounded-xl bg-surface-light border border-glass-border text-sm font-semibold text-text-primary hover:border-primary/40 transition-colors"
        >
          {t('captionComposer.done')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
